import { useEffect } from 'react';
import { SITE } from '../../config/site';

/**
 * Composant SEO Head pour l'optimisation des moteurs de recherche
 * Version compatible React 19 - utilise document.head directement
 */
const SEOHead = ({ 
  title, 
  description, 
  keywords, 
  canonical, 
  image,
  type = 'website',
  author = SITE.name,
  locale = 'fr_FR',
  siteName = SITE.name,
  noindex = false
}) => {
  // URL par défaut pour les images et canonical (src/config/site.js)
  const baseUrl = SITE.website;
  // Image de partage aux couleurs de la plateforme (1200 × 630, format des aperçus WhatsApp, Facebook, LinkedIn)
  const defaultImage = `${baseUrl}/og-image.jpg`;

  // Titre d'onglet : « Page | Nom du site » (sans répéter le nom s'il figure déjà dans le titre)
  const cleanTitle = title ? title.replace(/\s*[-|–]\s*Community ImpactInvest\s*$/i, '').trim() : '';
  const seoTitle = cleanTitle ? `${cleanTitle} | ${siteName}` : `${siteName} : investissement à impact social au Niger`;
  const seoDescription = description || SITE.description;
  // Image et adresse canonique toujours absolues, sur le domaine officiel : une même page servie par une autre
  // adresse (Tailscale, aperçu) n'est pas vue comme un doublon par les moteurs de recherche
  const absolute = (url) => (url && url.startsWith('/') ? `${baseUrl}${url}` : url);
  const seoImage = absolute(image) || defaultImage;
  const currentPath = typeof window !== 'undefined' ? window.location.pathname.replace(/(.)\/+$/, '$1') : '/';
  const seoCanonical = absolute(canonical) || `${baseUrl}${currentPath}`;
  const seoRobots = noindex ? 'noindex, follow' : 'index, follow';
  const seoKeywords = keywords || 'investissement impact social niger finance durable développement économique afrique projet responsable';

  useEffect(() => {
    // Gestion du titre
    if (document.title !== seoTitle) {
      document.title = seoTitle;
    }

    // Fonction pour créer ou mettre à jour un meta tag
    const setMetaTag = (name, content, property = false) => {
      if (!content) return;
      
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector);
      
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      
      meta.setAttribute('content', content);
    };

    // Fonction pour gérer les liens
    const setLinkTag = (rel, href) => {
      if (!href) return;
      
      let link = document.querySelector(`link[rel="${rel}"]`);
      
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      
      link.setAttribute('href', href);
    };

    // Meta tags basiques
    setMetaTag('description', seoDescription);
    setMetaTag('keywords', seoKeywords);
    setMetaTag('author', author);
    setMetaTag('robots', seoRobots);

    // Open Graph
    setMetaTag('og:title', seoTitle, true);
    setMetaTag('og:description', seoDescription, true);
    setMetaTag('og:image', seoImage, true);
    setMetaTag('og:type', type, true);
    setMetaTag('og:locale', locale, true);
    setMetaTag('og:site_name', siteName, true);
    if (seoCanonical) {
      setMetaTag('og:url', seoCanonical, true);
    }

    // Twitter Cards
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', seoTitle);
    setMetaTag('twitter:description', seoDescription);
    setMetaTag('twitter:image', seoImage);

    // Liens canoniques
    if (seoCanonical) {
      setLinkTag('canonical', seoCanonical);
    }

    // Nettoyage au démontage du composant
    return () => {
      // Optionnel: restaurer le titre par défaut si nécessaire
    };
  }, [seoTitle, seoDescription, seoImage, seoCanonical, seoRobots, seoKeywords, author, type, locale, siteName]);

  // Ce composant ne rend rien visuellement
  return null;
};

export default SEOHead;